import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import {
  CouponCreateType,
  CouponCustomerInfoType,
  IConditionIdsItem,
  ICreateCouponWithExistingConditionsForm,
  IEditPromotionCouponForm,
  IEventIdsItem,
  IPromotionConditionDetail,
  IPromotionCouponDetail,
  UserType,
} from "interfaces";
import { HttpError, useOne, useUpdate } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { Label } from "components/label";
import Select from "components/select";
import { useState } from "react";
import { endpointService } from "components/shared/services/endpointService";
import { Link, useNavigate } from "react-router-dom";
import Button from "components/button";
import toast from "react-hot-toast";
import { FullScreenLoading } from "components/fullScreenLoading";
import { EditPromotionCouponSchema } from "./schema";
import { Tag } from "components/tag";
import { SwitchButton } from "components/switchButton";
import Input from "components/input";
import { CollapsibleSection } from "components/collapsibleSection";
import { COUPON_CUSTOMER_TYPES } from "utills/constants";
import Textarea from "components/textarea";
import { canAccessByType } from "utills/helpers";
import { ViewConditionSetting } from "../../condition/detail";

export const EditPromotionCouponForm = ({
  defaultCouponData,
}: {
  defaultCouponData: IPromotionCouponDetail;
}) => {
  const { mutate, isLoading: isLoading } = useUpdate();
  const navigate = useNavigate();
  const [isShowCustomerInfo, setIsShowCustomerInfo] = useState<boolean>(
    defaultCouponData?.customer_info_type ||
      defaultCouponData?.customer_info_value
      ? true
      : false
  );
  const [isShowCouponDisplay, setIsShowCouponDisplay] = useState<boolean>(
    defaultCouponData?.displayed_on_actiup ||
      defaultCouponData?.displayed_on_whitelabel
  );
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IEditPromotionCouponForm, HttpError, IEditPromotionCouponForm>({
    defaultValues: {
      is_active: defaultCouponData?.is_active ?? false,
      customer_info_type: defaultCouponData?.customer_info_type ?? null,
      customer_info_value: defaultCouponData?.customer_info_value ?? "",
      displayed_on_actiup: defaultCouponData?.displayed_on_actiup ?? false,
      displayed_on_whitelabel:
        defaultCouponData?.displayed_on_whitelabel ?? false,
      max_times_used: defaultCouponData?.max_times_used ?? null,
      rule_id: defaultCouponData?.condition?.id ?? "",
      short_desc_en: defaultCouponData?.short_desc_en ?? "",
      short_desc_vi: defaultCouponData?.short_desc_vi ?? "",
    },
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver = zodResolver(EditPromotionCouponSchema());
      return createResolver(values, context, options);
    },
  });

  const { data: conditionData, isFetching: isFetchingConditionIds } = useOne<
    IConditionIdsItem[]
  >({
    resource: endpointService.getPromotionEndPoint("getConditionDropdown", {
      eventId: "all",
    }),
    id: "",
    meta: {
      resource: "data",
    },
  });

  const { data: conditionDetail, isFetching: isFetchingConditionDetail } =
    useOne<IPromotionConditionDetail>({
      resource: endpointService.getPromotionEndPoint("getConditionDetail", {
        conditionId: String(watch("rule_id")),
      }),
      id: "",
      meta: {
        resource: "data",
      },
      queryOptions: {
        enabled: !!watch("rule_id"),
      } as any,
    });

  const conditionDetailData = conditionDetail?.data ?? null;

  const onSubmit = (data: IEditPromotionCouponForm) => {
    const submitValues = { ...data };

    mutate(
      {
        resource: endpointService.getPromotionEndPoint(
          "updatePromotionCoupon",
          {
            couponId: defaultCouponData?.id,
          }
        ),
        id: "",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          navigate(`/promotion/coupon/${defaultCouponData?.id}`);
        },
      }
    );
  };

  const handleCustomerInfoToggle = (checked: boolean) => {
    setIsShowCustomerInfo(checked);
    if (checked) {
      setValue(
        "customer_info_type",
        defaultCouponData?.customer_info_type ?? CouponCustomerInfoType.CODE
      );
      setValue(
        "customer_info_value",
        defaultCouponData?.customer_info_value ?? ""
      );
    } else {
      setValue("customer_info_type", null);
      setValue("customer_info_value", null);
    }
  };

  const handleCouponDisplayToggle = (checked: boolean) => {
    setIsShowCouponDisplay(checked);
    setValue("displayed_on_actiup", false);
    setValue("displayed_on_whitelabel", false);
  };

  const conditionIds = (conditionData?.data ?? []).map(
    (condition: IConditionIdsItem) => ({
      value: condition.id,
      label: condition.note,
    })
  );

  console.log(errors);

  return (
    <form className="w-full h-full" onSubmit={handleSubmit(onSubmit)}>
      <div className="w-full flex flex-col gap-y-2 sm:gap-y-4">
        <div className="w-full box-container">
          <div className="grid gap-x-4 gap-y-4 grid-cols-8 text-sm">
            <div className="col-span-full flex items-center justify-between">
              <p className="font-bold text-base">Coupon Code</p>
              <Controller
                control={control}
                name="is_active"
                render={({ field: { onChange, value = true, ref } }) => (
                  <SwitchButton checked={value} onChange={onChange} />
                )}
              />
            </div>
            <div className="col-span-full">
              <Tag
                text={defaultCouponData?.code}
                customClassName="text-brand-secondary-500 bg-brand-secondary-300 ring-brand-secondary-300"
              />
            </div>
            <div
              className={
                canAccessByType(UserType.ADMIN)
                  ? "col-span-full"
                  : "col-span-full sm:col-span-4"
              }
            >
              <Label id="rule_id" name="Condition Name" />
              <Controller
                control={control}
                name="rule_id"
                render={({ field: { onChange, value = "", ref } }) => {
                  return (
                    <Select
                      key="rule_id"
                      inputRef={ref}
                      options={conditionIds}
                      id="condition_id"
                      placeholder="Select Condition"
                      defaultValue={
                        conditionIds.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        ) ?? ""
                      }
                      value={conditionIds.find(
                        (selected: { label: string; value: any }) =>
                          selected.value === value
                      )}
                      onChange={(selected: { label: string; value: any }) => {
                        onChange(selected?.value);
                      }}
                      error={errors["rule_id"]}
                    />
                  );
                }}
              />
              {errors["rule_id"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["rule_id"].message}
                  </span>
                </p>
              ) : null}
            </div>

            <div className="col-span-full sm:col-span-4">
              <Label required id="max_time_to_use" name="Max Time To Use" />
              <Input
                id="max_times_used"
                type="number"
                error={errors["max_times_used"]}
                placeholder="Max Time To Use"
                {...register("max_times_used", {
                  valueAsNumber: true,
                })}
              />
              {errors["max_times_used"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["max_times_used"].message}
                  </span>
                </p>
              ) : null}
            </div>
            {canAccessByType(UserType.ADMIN) && (
              <div className="col-span-full sm:col-span-4">
                <div className="mb-3">
                  <Input
                    type="checkbox"
                    label="Customer info"
                    id="customerInfo"
                    checked={isShowCustomerInfo}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleCustomerInfoToggle(e.target.checked)
                    }
                  />
                </div>
                {isShowCustomerInfo ? (
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <Controller
                        control={control}
                        name="customer_info_type"
                        render={({ field: { onChange, value = "", ref } }) => {
                          return (
                            <Select
                              key="customer_info_type"
                              inputRef={ref}
                              options={COUPON_CUSTOMER_TYPES}
                              id="customer_info_type"
                              placeholder="Customer info type"
                              value={
                                COUPON_CUSTOMER_TYPES.find(
                                  (selected: { label: string; value: any }) =>
                                    selected.value === value
                                ) ?? ""
                              }
                              onChange={(selected: {
                                label: string;
                                value: any;
                              }) => {
                                onChange(selected?.value);
                              }}
                              error={errors["customer_info_type"]}
                            />
                          );
                        }}
                      />
                      {errors["customer_info_type"] ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {errors["customer_info_type"].message}
                          </span>
                        </p>
                      ) : null}
                    </div>
                    <div className="flex-1">
                      <Input
                        id="customer_info_value"
                        type="text"
                        error={errors["customer_info_value"]}
                        placeholder="Customer Info Value"
                        {...register("customer_info_value")}
                      />
                      {errors["customer_info_value"] ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {errors["customer_info_value"].message}
                          </span>
                        </p>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <></>
                )}
              </div>
            )}
          </div>
        </div>
        {conditionDetailData && (
          <ViewConditionSetting conditionDetailData={conditionDetailData} />
        )}
        {canAccessByType(UserType.ADMIN) && (
          <CollapsibleSection label="Additional Set-up" defaultOpen={false}>
            <div className="box-container">
              <div className="grid gap-x-4 gap-y-4 grid-cols-8 text-sm">
                <div className="col-span-full">
                  <Input
                    type="checkbox"
                    label="Coupon Display"
                    id="couponDisplay"
                    checked={isShowCouponDisplay}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleCouponDisplayToggle(e.target.checked)
                    }
                  />
                </div>
                {isShowCouponDisplay && (
                  <>
                    <div className="col-span-full flex items-center gap-x-4">
                      <Controller
                        control={control}
                        name="displayed_on_actiup"
                        render={({
                          field: { onChange, value = true, ref },
                        }) => (
                          <Input
                            type="checkbox"
                            label="Display on ActiUp"
                            id="displayed_on_actiup"
                            checked={value}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => onChange(e.target.checked)}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name="displayed_on_whitelabel"
                        render={({
                          field: { onChange, value = true, ref },
                        }) => (
                          <Input
                            type="checkbox"
                            label="Display on Whitelabel"
                            id="displayed_on_whitelabel"
                            checked={value}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => onChange(e.target.checked)}
                          />
                        )}
                      />
                    </div>
                    <div className="col-span-full">
                      <Label id="short_desc_vi" name="Coupon Terms of Use" />
                      <div>
                        <Controller
                          control={control}
                          name="short_desc_vi"
                          render={({
                            field: { onChange, value = "", ref },
                          }) => {
                            return (
                              <div className="relative">
                                <Textarea
                                  id="coupons.short_desc_vi"
                                  placeholder="Coupon Terms of Use (vi)"
                                  className="pr-10"
                                  error={errors["short_desc_vi"]}
                                  onChange={onChange}
                                  value={value}
                                />
                                <img
                                  src="/images/flags/circle-vi.png"
                                  alt="Language Icon"
                                  className="size-5 absolute top-3 right-3"
                                />
                              </div>
                            );
                          }}
                        />
                        {errors["short_desc_vi"] ? (
                          <p className="error-text">
                            <span className="font-medium">
                              {errors["short_desc_vi"]?.message}
                            </span>
                          </p>
                        ) : null}
                      </div>
                      <div className="mt-4">
                        <Controller
                          control={control}
                          name="short_desc_en"
                          render={({
                            field: { onChange, value = "", ref },
                          }) => {
                            return (
                              <div className="relative">
                                <Textarea
                                  id="short_desc_en"
                                  placeholder="Coupon Terms of Use (en)"
                                  className="pr-10"
                                  error={errors["short_desc_en"]}
                                  onChange={onChange}
                                  value={value}
                                />
                                <img
                                  src="/images/flags/circle-en.png"
                                  alt="Language Icon"
                                  className="size-5 absolute top-3 right-3"
                                />
                              </div>
                            );
                          }}
                        />
                        {errors["short_desc_en"] ? (
                          <p className="error-text">
                            <span className="font-medium">
                              {errors["short_desc_en"]?.message}
                            </span>
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </CollapsibleSection>
        )}
        <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
          <div className="flex items-center justify-end gap-x-4">
            <Link to={`/promotion/coupon/${defaultCouponData?.id}`}>
              <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                Cancel
              </Button>
            </Link>
            <Button className="w-20" type="submit">
              Save
            </Button>
          </div>
        </div>
        {isLoading || isFetchingConditionDetail ? <FullScreenLoading /> : <></>}
      </div>
    </form>
  );
};
